import React, { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const STATUSES = ['Planned', 'Active', 'Completed', 'Cancelled'];

type Props = { projectId?: string };

export const ManpowerPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const packagesQ = useQuery({
    queryKey: ['cep-packages', projectId],
    queryFn: () => epcService.getExecutionPackages(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const manpowerQ = useQuery({
    queryKey: ['cep-manpower', projectId],
    queryFn: () => epcService.getCepManpower(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createManpower({ ...values, projectId }),
    onSuccess: () => {
      message.success('Crew assigned to package');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-manpower'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for manpower assignments</Tag>;

  const rows = (manpowerQ.data as any[]) || [];
  const packages = (packagesQ.data as any[]) || [];
  const pkgLabel = (id: unknown) =>
    packages.find((p: any) => String(p.id) === String(id))?.package_number || String(id || '—');

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          Assign crew
        </Button>
        <Tag color="geekblue">CEP Wave 3</Tag>
        <Tag>Assignments belong to an Execution Package</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={manpowerQ.isLoading}
        dataSource={rows}
        columns={[
          {
            title: 'Package',
            dataIndex: 'package_id',
            width: 120,
            render: (v: string) => pkgLabel(v),
          },
          { title: 'Crew', dataIndex: 'crew_name', width: 120 },
          { title: 'Trade', dataIndex: 'trade_code', width: 100 },
          { title: 'Headcount', dataIndex: 'headcount', width: 90 },
          { title: 'Shift', dataIndex: 'shift_code', width: 80 },
          { title: 'Date', dataIndex: 'assignment_date', width: 110 },
          { title: 'Status', dataIndex: 'status', width: 100 },
        ]}
      />
      <Modal
        title="Assign crew to package"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{
            status: 'Planned',
            headcount: 1,
            assignmentDate: new Date().toISOString().slice(0, 10),
          }}
        >
          <Form.Item name="packageId" label="Execution package" rules={[{ required: true }]}>
            <Select
              options={packages.map((p: any) => ({
                value: String(p.id),
                label: p.package_number,
              }))}
            />
          </Form.Item>
          <Form.Item name="crewName" label="Crew">
            <Input />
          </Form.Item>
          <Form.Item name="tradeCode" label="Trade">
            <Input placeholder="Pipefitter / Welder…" />
          </Form.Item>
          <Form.Item name="contractorName" label="Contractor">
            <Input />
          </Form.Item>
          <Form.Item name="subcontractorName" label="Subcontractor">
            <Input />
          </Form.Item>
          <Form.Item name="headcount" label="Headcount">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="shiftCode" label="Shift">
            <Input placeholder="Day / Night" />
          </Form.Item>
          <Form.Item name="plannedHours" label="Planned hours">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="assignmentDate" label="Date" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={STATUSES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ManpowerPanel;
